// Shared TypeScript types for ptc-conversions. Mirrors the backend C# models so
// the wire shape stays consistent end-to-end.

export interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
  /** Machine-readable error code (api/Services/ApiErrorCodes.cs) — mapped to a sentence by lib/apiErrors. */
  code?: string;
  /** A specific to show inside the coded sentence (lib/apiErrors). */
  detail?: string | null;
}

/**
 * One row of the client directory — what the client lookup shows and picks
 * (CONVERSIONS_SPEC.md §3). See api/Models/ClientSummary.cs.
 *
 * `clientId` is the Dataverse account GUID and is never rendered as a form
 * value: the picker displays `name` and keeps the id behind it.
 */
export interface ClientSummary {
  clientId: string;
  name: string;
  /** DBA / fictitious name, present only when it differs from `name`. */
  dbaName?: string | null;
  isActive: boolean;
}

/**
 * Per-client Xero chart-of-accounts mapping. Backed by the `clientGlMaps` Cosmos
 * container; one row per client. See api/Models/ClientGlMap.cs for field semantics.
 */
/**
 * The Xero organisation a client's bookkeeping posts to (C8), from
 * `GET /api/clients/{clientId}/xero-tenant`. See api/Models/XeroTenantLink.cs.
 *
 * ⚠ `null` is the NORMAL answer, not an error: `core.XeroTenant` is empty in
 * production, so every client is unlinked today. Render the unlinked state as
 * ordinary information — never as a failure. The endpoint returns 200 with
 * `data: null` for exactly this reason.
 */
export interface XeroTenantLink {
  tenantId: string;
  tenantType?: string | null;
  status?: string | null;
}

/** Where the Xero row's answer came from, and how old the client records behind it are (CNV-004). */
export interface XeroTenantProvenance {
  source: 'ptc-core';
  checkedAtUtc: string;
  /** ptc-core's last successful Dataverse refresh; null when it could not be read (render as unknown, never fresh). */
  clientRecordsRefreshedAtUtc: string | null;
}

export interface XeroTenantResult {
  link: XeroTenantLink | null;
  /** Null from an API build older than CNV-004. */
  provenance: XeroTenantProvenance | null;
}

export interface ClientGlMap {
  id: string;
  clientId: string;
  /**
   * The extensible surface (CONVERSIONS_SPEC.md §5.4): canonical role name → the
   * client's Xero account code. Templates read this, not the typed fields below.
   *
   * The backend projects any populated typed field into `roles` on read, so a map
   * saved before roles existed still resolves here. Keys are NOT restricted to
   * `GL_ROLES` — a client may map a role PTC has not thought of.
   */
  roles?: Record<string, string>;
  /** Role name → the account's display name. Display-only; Xero imports on the code. */
  roleNames?: Record<string, string>;
  /**
   * The eight legacy typed `…AccountCode` properties were retired here when the G/L
   * mapping screen moved to the role table. They still exist on `api/Models/ClientGlMap.cs`
   * and MUST stay there: `XeroCsvBuilder` reads `SalesTaxPayableAccountCode` and friends
   * directly, and `GlMapService.UpsertAsync` → `GlMapRoles.Normalize` mirrors `roles` back
   * onto them on every write. The frontend simply no longer speaks that dialect — it reads
   * and writes `roles` only, and the backend keeps both representations in agreement.
   *
   * The extra properties still arrive on the wire; TypeScript ignores what it does not
   * declare, so nothing here needs to change when they are eventually dropped.
   */
  notes?: string;
  /**
   * The client's chart (docs/client-chart-and-sales-split.md §1). Absent from a save keeps the stored chart on the
   * server, so this screen always sends it.
   */
  accounts?: GlAccount[];
  /** A client-defined mapping row's label, by its custom key in `roles`. */
  rowLabels?: Record<string, string>;
  /**
   * The client's own journal template (docs/client-journal-template.md §A). Stored null = the DEFAULT (the seven POS
   * boxes). On a save, absent keeps the stored one and `{ lines: [] }` returns the client to the default.
   */
  journalTemplate?: ClientJournalTemplate | null;
  updatedByName?: string;
  updatedAt?: string;
}

/** One account in a client's chart. Mirrors api/Models/GlAccount.cs. */
export interface GlAccount {
  code: string;
  name?: string | null;
  /** Xero's account type (`GL_ACCOUNT_TYPES`), or none yet. */
  type?: string | null;
  taxType?: string | null;
  status: 'ACTIVE' | 'ARCHIVED';
  /** `typed` = added here; `xero` = from the pull (not built yet). */
  source: 'typed' | 'xero';
  xeroAccountId?: string | null;
  nameOverride?: string | null;
  lastSyncedAt?: string | null;
  /** The codes it had before (§B3). Server-owned. */
  previousCodes?: AccountCodeChange[] | null;
}

export type TemplateLineSide = 'debit' | 'credit';
export type TemplateLineSource = 'pos' | 'salesTaxReturn' | 'bank' | 'manual';

/** One line of a client's journal template. Its account is `roles[key]`, never stored here. Mirrors api/Models/JournalTemplate.cs. */
export interface TemplateLine {
  key: string;
  label?: string | null;
  side: TemplateLineSide;
  source: TemplateLineSource;
  hint?: string | null;
  /** The amount also posts, on the other side, to this role (the collection allowance pair). */
  offsetKey?: string | null;
  /** The extraction engine's registry codes this line sums; a pick wins over the hint (core's P2 plan §2b). */
  registryField?: string[] | null;
  /** The bookkeeper's note on the line, in the client's own words (wave 4 §7: DP-P2-4's "confirm", Moretti's override). */
  note?: string | null;
}

export interface ClientJournalTemplate {
  lines: TemplateLine[];
}

/** One code change (§B3). Server-owned. */
export interface AccountCodeChange {
  from: string;
  to: string;
  changedAt: string;
  changedByName?: string | null;
}

/**
 * Canonical GL role vocabulary (CONVERSIONS_SPEC.md §5.4). Mirrors
 * api/Models/GlRoles.cs. A starting set, not a closed enum: nothing validates
 * against it on either side.
 */
export const GL_ROLES = [
  'salesRevenue',
  'tipsPayable',
  'salesTaxPayable',
  'creditCardClearing',
  'cashOnHand',
  'merchantFees',
  'collectionAllowanceIncome',
  'penaltyExpense',
  'interestExpense',
  'solidWasteFeePayable',
  'documentaryStampPayable',
  'bankAccount',
] as const;
export type GlRole = (typeof GL_ROLES)[number];

/**
 * One line of a manual journal. Exactly one of `debit` / `credit` is set and it is
 * strictly positive — a bookkeeper enters a debit *or* a credit, so the form asks
 * that way rather than teaching people to negate. See api/Models/ManualJournal.cs.
 */
export interface JournalLine {
  accountCode: string;
  accountName?: string | null;
  description?: string | null;
  debit?: number | null;
  credit?: number | null;
  taxRate?: string | null;
}

export type JournalStatus = 'draft' | 'exported';

/**
 * A Xero manual journal in the shape Xero actually uses (spec §5). ONE date — not a
 * period — and ordered, independent lines that are never netted by account code.
 */
export interface ManualJournal {
  id: string;
  clientId: string;
  /** `yyyy-MM-dd`. Required, and derived from nothing. */
  journalDate: string;
  narration: string;
  lines: JournalLine[];
  sourceDocumentIds?: string[];
  templateId?: string | null;
  /** The DR-15 extraction run these lines were proposed from (B1). */
  proposedFromExtractionRunId?: string | null;
  /** The DR-15 figures actually proposed, possibly edited from the extraction's. */
  proposedFigures?: Partial<Dr15Extract> | null;
  /** The POS template's figure boxes as typed, proposed or not. Kept with the journal since deploy #23. */
  posFigures?: PosPeriodTotals | null;
  /** On the client's own template: key → the figure in that box, a blank box as null (§A5). Beside `posFigures`. */
  lineFigures?: Record<string, number | null> | null;
  /** The code changes its lines followed (§B3). Server-owned. */
  accountCodeChanges?: AccountCodeChange[] | null;
  /** B2: the reference its exports carry in the narration, assigned at first export. */
  xeroReference?: string | null;
  /** B2: who confirmed it is in Xero. Withdrawn ones are kept; the active one is the last not withdrawn. */
  xeroImportConfirmations?: XeroImportConfirmation[];
  /** B3: this draft corrects that exported journal. */
  supersedesJournalId?: string | null;
  /** B3: this exported journal's correction has been exported; it is kept, never deleted. */
  supersededByJournalId?: string | null;
  status: JournalStatus;
  createdByName?: string | null;
  createdAt?: string;
  updatedAt?: string | null;
  exportedAt?: string | null;
  /** Accuracy Q3: who ticked "I checked these figures against the return" on Export, and when. Server-owned. */
  figuresConfirmedAt?: string | null;
  figuresConfirmedByName?: string | null;
  isDeleted?: boolean;
  /** When and by whom it was removed (server-owned; null on a journal removed before this was recorded). */
  deletedAt?: string | null;
  deletedByName?: string | null;
}

/** B2: one confirmation that an exported journal is in Xero. Mirrors api/Models/ManualJournal.cs. */
export interface XeroImportConfirmation {
  confirmedAt: string;
  confirmedByName?: string | null;
  xeroJournalNumber?: string | null;
  withdrawnAt?: string | null;
  withdrawnByName?: string | null;
}

/** One template offered by the picker. Mirrors api/Models/JournalTemplates.cs. */
export interface JournalTemplateInfo {
  id: string;
  label: string;
  description: string;
  /** Roles that must be mapped before this template can propose anything. */
  requiredRoles: string[];
}

/** POS period totals, read off the client's POS statement by staff (spec §5.5). */
export interface PosPeriodTotals {
  netSales?: number | null;
  salesTax?: number | null;
  tips?: number | null;
  cardSettlement?: number | null;
  cash?: number | null;
  merchantFees?: number | null;
  collectionAllowance?: number | null;
}

export interface JournalProposalRequest {
  journalDate?: string;
  narration?: string;
  dr15?: Partial<Dr15Extract>;
  pos?: PosPeriodTotals;
  /** On a client with its own template, instead of `pos` (§A5). */
  lineFigures?: Record<string, number | null>;
}

/**
 * What a template proposes. Nothing here is committed — templates never post, they
 * fill a form. A proposal is allowed to be unbalanced; the editor shows the
 * difference and blocks export until it is zero.
 */
export interface JournalProposal {
  templateId: string;
  journalDate: string;
  narration: string;
  lines: JournalLine[];
  totalDebit: number;
  totalCredit: number;
  /** `totalDebit − totalCredit`. Zero means balanced. */
  difference: number;
  warnings: string[];
  /** The client's remembered split, when this proposal's sales line was spread by it (POS; §3). */
  appliedSalesSplit?: SalesSplit | null;
  /** The client has a split that could not be applied (an account archived or gone), so one sales line was proposed. */
  salesSplitStale?: boolean;
}

/** A client's remembered sales split (docs/client-chart-and-sales-split.md §3). Mirrors api/Models/SalesSplit.cs. */
export interface SalesSplit {
  shares: { code: string; percent: number }[];
  /** `learned-from-journal:<journalId>`. */
  origin: string;
  learnedAt: string;
  learnedByName?: string | null;
}

/**
 * Categorized-upload document categories — the canonical wire form, matching the
 * backend's DocumentCategories (api/Models/DocumentCategories.cs). Extending the
 * set means updating both sides plus the i18n labels.
 */
export const DOCUMENT_CATEGORIES = [
  'bankStatement',
  'payrollReport',
  'posReport',
  'dr15Return',
] as const;
export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number];

/**
 * Metadata for one stored categorized upload. Backed by the `uploadedDocuments`
 * Cosmos container; bytes live in Blob storage. See api/Models/UploadedDocument.cs
 * and docs/categorized-upload-intake.md.
 */
export interface UploadedDocument {
  id: string;
  clientId: string;
  category: DocumentCategory;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  blobName: string;
  /** Parsed row count — bankStatement uploads only (parse-validated at intake). */
  transactionCount?: number | null;
  uploadedAt: string;
  uploadedByName?: string | null;
  /** Removed, never deleted (DP-CNV-BACKUP): kept and restorable. */
  isDeleted?: boolean;
  deletedAt?: string | null;
  deletedByName?: string | null;
}

/**
 * Reported amounts extracted from a filed FL DR-15. Mirrors the bridge contract
 * with ptc-statetax's `complianceTaskExtracts` cache.
 */
export interface Dr15Extract {
  clientId: string;
  grossSales?: number;
  exemptSales?: number;
  taxableSales?: number;
  taxCollected?: number;
  surtax?: number;
  penalty?: number;
  interest?: number;
  collectionAllowance?: number;
  totalDue?: number;
  /** The collection period's last day (yyyy-MM-dd), read from the form; the journal date defaults to it. */
  periodEnd?: string | null;
  extractedFrom?: string;
  extractionConfidence?: number;
}

/**
 * Outcome of one DR-15 extraction (Slice 2). Wraps the extract with honesty
 * metadata: whether the printed figures reconciled (totalDue = taxCollected −
 * collectionAllowance + penalty + interest), the business name printed on the
 * return (AI path only), and which pipeline produced the figures.
 */
export interface Dr15ExtractionResult {
  extract: Dr15Extract;
  crossCheckPassed: boolean;
  businessName?: string | null;
  method: 'pdfpig-regex' | 'pdfpig+gpt-4o' | 'document-intelligence+gpt-4o';
  /** The audit row that keeps these figures; the journal page reads them back by it (B1). */
  extractionRunId?: string | null;
}

/** Mission item 4: the extractor's record against exported journals. Mirrors api/Models/ExtractionGrading.cs. */
export interface ExtractionAccuracyReport {
  verdict: 'NO DATA' | 'graded';
  pairs: number;
  allFiguresExactPct: number | null;
  totalDueExactPct: number | null;
  totalDueWithinTolerancePct: number | null;
  byFigure: { figure: string; count: number; exactPct: number; withinTolerancePct: number; meanAbsError: number }[];
  byMethod: { method: string; pairs: number; allFiguresExactPct: number }[];
  notInJournal: string[];
  shadowsWithoutDocumentId: number;
  ambiguousJournals: number;
  journalsOfAnotherTemplate: number;
  detail: unknown[];
  /** Accuracy per client (docs/accuracy-per-client.md): a row per client × source kind, longest streak first. */
  byClient: ClientAccuracy[];
  /** How many exact pairs in a row make a source dependable (Q1, lean: 6). */
  dependableAfter: number;
  /** ptc-core calls the caller an admin; only then is `dependable` set (Q4). */
  viewerIsAdmin: boolean;
  /** The stored grades (accuracy §2): an admin's only; absent for anyone else. */
  ledger?: GradeLedgerView | null;
}

/** One stored grade (accuracy §2). Mirrors api/Models/StoredGrades.cs. */
export interface StoredGrade {
  id: string;
  clientId: string;
  sourceKind: string;
  month: string;
  extractionRunId: string;
  journalId: string;
  exportedAt?: string | null;
  gradedCount: number;
  exactCount: number;
  missClass?: string | null;
  figure?: string | null;
  gradedAt: string;
  runId: string;
  disposition?: { rulingId: string; kind: string; at: string } | null;
  superseded: boolean;
}

export interface GradingRun {
  id: string;
  sourceKind: string;
  month: string;
  coveredThrough?: string | null;
  graded: number;
  misses: number;
  superseded: number;
  ranAt: string;
  ranByName?: string | null;
}

export type RulingKind = 'parser-fix' | 'source-defect' | 'accepted';

export interface GradeRuling {
  id: string;
  sourceKind: string;
  missClass: string;
  kind: RulingKind;
  fixDeployedAt?: string | null;
  note?: string | null;
  ruledAt: string;
  ruledByName?: string | null;
  applied: number;
}

export interface GradeLedgerView {
  watermark?: GradingRun | null;
  unruled: Record<string, number>;
  regressions: StoredGrade[];
  rulings: GradeRuling[];
}


export type MissClass = 'wrong-figure' | 'missing-figure' | 'unconfirmed';

/** One graded pair on a client's row. Mirrors `StreakDot` in api/Models/ExtractionGrading.cs. */
export interface StreakDot {
  journalId: string;
  exportedAt?: string | null;
  /** Null when the pair counts toward the streak. */
  missClass?: MissClass | null;
  figure?: string | null;
}

export interface ClientAccuracy {
  clientId: string;
  sourceKind: 'dr15';
  graded: number;
  streak: number;
  toDependable: number;
  /** Null for anyone ptc-core does not call an admin: the badge is admin-visible only. */
  dependable?: boolean | null;
  lastMiss?: StreakDot | null;
  /** Newest first. */
  recent: StreakDot[];
}

/** One extraction run's kept figures, read back for a journal to be proposed from (B1). */
export interface ExtractionRun {
  id: string;
  clientId: string;
  documentId?: string | null;
  sourceFilename?: string | null;
  method?: string | null;
  confidence?: number | null;
  crossCheckPassed?: boolean | null;
  completedAt?: string | null;
  extract: Dr15Extract;
}

// ── Conversion history (CONVERSIONS_SPEC.md §8) ──────────────────────────────

/** The three flows a conversion can come from. Mirrors `ConversionFlows` on the server. */
export type ConversionFlow = 'bankStatement' | 'accountTransactions' | 'manualJournal';

export const CONVERSION_FLOWS: readonly ConversionFlow[] = [
  'bankStatement',
  'accountTransactions',
  'manualJournal',
] as const;

/**
 * One generated output, retained so it can be downloaded again.
 *
 * ⚠ `blobPath` being absent is a REAL state, not a gap: the row was written but the file
 * could not be retained. The UI must offer no download for those rather than a link that
 * 404s — "recorded but not retained" and "never happened" are different facts, and only
 * the first is something to show a client.
 *
 * ⚠ And the history is **best-effort, not a ledger**. The server records fail-open so a
 * storage outage never costs a bookkeeper their conversion; a missing row therefore means
 * "we failed to record it", never "it did not happen". Do not phrase any UI as proof of a
 * negative.
 */
export interface ConversionRecord {
  id: string;
  clientId: string;
  flow: ConversionFlow;
  fileName: string;
  /** Null when the file was not retained — see the type remarks. */
  blobPath?: string | null;
  /** Manual journals: the single date the journal posts on. */
  journalDate?: string | null;
  /** Bank flows: the span actually present in the file. */
  periodStart?: string | null;
  periodEnd?: string | null;
  sourceDocumentIds?: string[];
  journalId?: string | null;
  lineCount?: number | null;
  totalDebit?: number | null;
  totalCredit?: number | null;
  generatedByName?: string | null;
  /** When the conversion was produced — NOT the period it covers. */
  generatedAt: string;
  sizeBytes?: number | null;
  /** The builder's one-line human summary. Free text; nothing parses it. */
  summary?: string | null;
}

/** Filters for the history list. All optional; they AND together on the server. */
export interface ConversionHistoryFilters {
  flow?: ConversionFlow;
  /** yyyy-MM-dd, bounds `generatedAt`. `to` is inclusive of its whole day. */
  from?: string;
  to?: string;
}

/** "Read the report" (wave 4 §5; api/Models/PosReportRead.cs). Figures arrive in `fields` when the engine reads (P2.5). */
export type PosReportReadStatus = 'notReadYet' | 'unknownKind' | 'read';

export interface PosReportField {
  code: string;
  /** A decimal STRING, never a float. */
  amount: string;
  page: number;
  section: string;
  label: string;
  evidence: string;
}

export interface PosReportReadResult {
  status: PosReportReadStatus;
  kind: string | null;
  kindLabel: string | null;
  registryVersion: string;
  period: { printedStart: string; printedEnd: string } | null;
  classesPrinted: { label: string; code: string | null }[];
  fields: PosReportField[];
  invariants: { id: string; holds: boolean }[];
}
